import { type RichContent } from '@wix/ricos';

import { useUniqueFieldSuffix } from '../contexts/unique-field-suffix-context';
import { type FieldError } from '../field-error/error-types';

export function getInputId(fieldId: string, uniqueId: string) {
  return `form-field-input-${fieldId}-${uniqueId}` as const;
}

export function getLabelId(fieldId: string, uniqueId: string) {
  return `form-field-label-${fieldId}-${uniqueId}` as const;
}

export function getPrefixId(fieldId: string, uniqueId: string) {
  return `form-field-prefix-${fieldId}-${uniqueId}` as const;
}

export function getDescriptionId(fieldId: string, uniqueId: string) {
  return `form-field-description-${fieldId}-${uniqueId}`;
}

export function getErrorId(fieldId: string, uniqueId: string) {
  return `form-field-error-${fieldId}-${uniqueId}`;
}

export function getAriaDescribedBy(
  errorId: string,
  descriptionId: string,
  error: FieldError | undefined,
  description: RichContent | undefined,
): string | undefined {
  return (
    [error && errorId, description && descriptionId]
      .filter(Boolean)
      .join(' ') || undefined
  );
}

export function getErrorDataHook(target: string) {
  return `field-error-${target}`;
}

export type FieldAttributes = {
  inputId: ReturnType<typeof getInputId>;
  labelId: ReturnType<typeof getLabelId>;
  prefixId: ReturnType<typeof getPrefixId>;
  descriptionId: ReturnType<typeof getDescriptionId>;
  errorId: ReturnType<typeof getErrorId>;
  ariaDescribedBy: ReturnType<typeof getAriaDescribedBy>;
};

export function useFieldAttributes(
  fieldId: string,
  error: FieldError | undefined,
  description: RichContent | undefined,
): FieldAttributes {
  const uniqueId = useUniqueFieldSuffix();
  const errorId = getErrorId(fieldId, uniqueId);
  const descriptionId = getDescriptionId(fieldId, uniqueId);

  return {
    inputId: getInputId(fieldId, uniqueId),
    labelId: getLabelId(fieldId, uniqueId),
    prefixId: getPrefixId(fieldId, uniqueId),
    descriptionId,
    errorId,
    ariaDescribedBy: getAriaDescribedBy(
      errorId,
      descriptionId,
      error,
      description,
    ),
  };
}
